import styled from '@emotion/styled';
import { BaseIcon, Divider, useTippy, tokens } from '@hotjar/hotsauce';
import { NavDropdownMenu, NavList } from '@hotjar/navigation/src/components/NavDropdown';
import type { EventEmitter, NavigationProps, Site } from '@hotjar/navigation/src/types';
import { Position } from '@hotjar/navigation/src/events/Position';
import type { NavigationEvent } from '@hotjar/navigation/src/events/types';
import { useTranslation } from 'react-i18next';
import { RoundIconButton } from '../RoundButton';
import { useHelpHotjarUrl } from '@hotjar/translations';

export interface HelpMenuProps {
  className?: string;
  emitter: EventEmitter;
  whatsNewCount?: NavigationProps['whatsNewCount'];
  currentSite: NavigationProps['currentSite'];
}

const eventPosition: NavigationEvent = Object.freeze({ position: Position.helpMenu });

// These urls are defined in packages/legacy/src/webapp/constants/support.ts
// We should extract those constants to a common area so we can import them in here as well.
// For now the ones we need are redefined here.
const GET_STARTED_URL = '/articles/360049416814';
const EXPLORE_COURSES_URL = 'https://learning.hotjar.com';
const SYSTEM_STATUS_URL = 'https://status.hotjar.com';

/**
 * Create a help url customized for the current site/user.
 * @param baseUrl The base url for the help centre section
 * @param currentSite The user's current site
 */
const buildHelpCentreURL = (baseUrl: string, currentSite: Site): string => {
  const url = new URL(baseUrl);

  if (currentSite?.id) {
    url.searchParams.append('is_verified', (currentSite.lastData !== null).toString());
  }

  return url.href;
};

const Dot = styled.div`
  position: absolute;
  top: 9px;
  right: 9px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: ${tokens.BRAND_COLORS.ember_700};
  border: 1px solid ${tokens.BRAND_COLORS.white};
`;

const Container = styled.div`
  position: relative;
`;

export const HelpMenu = ({
  className,
  emitter,
  whatsNewCount,
  currentSite,
}: HelpMenuProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const helpHotjarUrl = useHelpHotjarUrl();
  const tippyInstance = useTippy();

  const description = t('HelpMenu.tooltip');

  return (
    <NavDropdownMenu
      trigger={
        <Container>
          <RoundIconButton
            icon={<BaseIcon icon="help" pixelSize={20} />}
            description={description}
            tooltipText={description}
            kind="ghost"
            className={className}
          />
          {whatsNewCount && <Dot />}
        </Container>
      }
      onTrigger={() => {
        emitter.emit('clickedHelpMenu', { position: Position.primaryNavigation });
      }}
      onCreate={tippyInstance.set}
    >
      <NavList.LinkItem
        icon="help"
        onClick={(e) => {
          // In order to keep a visual consistency between the links, we're adding a
          // dummy href to this LinkItem. By calling e.preventDefault(), that href
          // won't be used and the onClick handler will be used instead.
          e.preventDefault();
          tippyInstance.close();
          emitter.emit('clickedGetHelp', eventPosition);
        }}
        href={buildHelpCentreURL(helpHotjarUrl, currentSite)}
        isExternal
      >
        {t('HelpMenu.getHelp')}
      </NavList.LinkItem>
      <NavList.LinkItem
        icon="book_open"
        onClick={() => {
          tippyInstance.close();
          emitter.emit('clickedBrowseGuides', eventPosition);
        }}
        href={buildHelpCentreURL(`${helpHotjarUrl}${GET_STARTED_URL}`, currentSite)}
        isExternal
      >
        {t('HelpMenu.browseGuides')}
      </NavList.LinkItem>
      <NavList.LinkItem
        icon="blog"
        onClick={() => {
          tippyInstance.close();
          emitter.emit('clickedExploreCourses', eventPosition);
        }}
        href={buildHelpCentreURL(EXPLORE_COURSES_URL, currentSite)}
        isExternal
      >
        {t('HelpMenu.exploreCourses')}
      </NavList.LinkItem>
      <Divider />
      <NavList.ItemWithCounter
        icon="list"
        count={whatsNewCount}
        onClick={() => {
          tippyInstance.close();
          emitter.emit('clickedProductUpdates', eventPosition);
        }}
      >
        {t('HelpMenu.productUpdates')}
      </NavList.ItemWithCounter>
      <NavList.LinkItem
        icon="info"
        onClick={() => {
          tippyInstance.close();
          emitter.emit('clickedSystemStatus', eventPosition);
        }}
        href={SYSTEM_STATUS_URL}
        isExternal
      >
        {t('HelpMenu.systemStatus')}
      </NavList.LinkItem>
    </NavDropdownMenu>
  );
};
